import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
    BadgeIndianRupee,
    CheckCircle2,
    CircleAlert,
    Contact,
    FileText,
    HeartHandshake,
    IdCard,
    Loader2,
    MapPin,
    Plus,
    RotateCcw,
    Send,
    Trash2,
    UploadCloud,
    UserRound,
    UsersRound,
    X
} from 'lucide-react';
import { confirmDialog, toast as notify } from '../../../components/notifications/notificationService';
import { isAuthenticated } from '../../../utils/auth';
import Sidebar from '../../../components/Sidebar/sidebar';
import Header from '../../../components/Header';
import './RetailClients.css';
import axios from 'axios';
import api from '../../../utils/api';
import { API_BASE_URL } from '../../../utils/apiBase';

const familyTemplate = [
    { relation: 'spouse', showDetails: false, name: '', phoneNumber: '', emailId: '', DOB: '', gender: '', occupation: '', PANNumber: '', aadharNumber: '' },
    { relation: 'father', showDetails: false, name: '', phoneNumber: '', emailId: '', DOB: '', gender: '', occupation: '', PANNumber: '', aadharNumber: '' },
    { relation: 'mother', showDetails: false, name: '', phoneNumber: '', emailId: '', DOB: '', gender: '', occupation: '', PANNumber: '', aadharNumber: '' },
    { relation: 'fatherInLaw', showDetails: false, name: '', phoneNumber: '', emailId: '', DOB: '', gender: '', occupation: '', PANNumber: '', aadharNumber: '' },
    { relation: 'motherInLaw', showDetails: false, name: '', phoneNumber: '', emailId: '', DOB: '', gender: '', occupation: '', PANNumber: '', aadharNumber: '' }
];

const initialFormData = {
    autoGeneratedId: '',
    clientName: '',
    phoneNumber: '',
    emailId: '',
    prospectAddress: '',
    pinCode: '',
    state: '',
    city: '',
    prospectDOB: '',
    prospectGender: '',
    maritalStatus: '',
    prospectOccupation: '',
    incomeYearly: '',
    aadharNumber: '',
    panNumber: '',
    aadharFileUpload: false,
    panFileUpload: false,
    aadharFile: null,
    panFile: null,
    familyMembers: familyTemplate,
    children: [],
    showChildren: false
};

const fallbackStates = [
    'Andhra Pradesh', 'Bihar', 'Delhi', 'Goa', 'Gujarat',
    'Haryana', 'Karnataka', 'Kerala', 'Maharashtra',
    'Punjab', 'Rajasthan', 'Tamil Nadu', 'Uttar Pradesh', 'West Bengal'
];

const labelMap = {
    name: 'Name',
    phoneNumber: 'Phone Number',
    emailId: 'Email ID',
    DOB: 'Date of Birth',
    gender: 'Gender',
    occupation: 'Occupation',
    PANNumber: 'PAN Number',
    aadharNumber: 'Aadhar Number'
};

const relationLabels = {
    spouse: 'Spouse',
    father: 'Father',
    mother: 'Mother',
    fatherInLaw: 'Father In Law',
    motherInLaw: 'Mother In Law'
};

const Client = () => {
    const [collapsed, setCollapsed] = useState(false);
    const [loading, setLoading] = useState(false);
    const [pageLoading, setPageLoading] = useState(true);
    const [errors, setErrors] = useState({});
    const [pinStatus, setPinStatus] = useState('');
    const [apiLocationSuccess, setApiLocationSuccess] = useState(true);
    const [fallbackCities, setFallbackCities] = useState([]);
    const [formData, setFormData] = useState(initialFormData);
    const fileRefs = useRef({});

    const requiredFields = useMemo(
        () => ['clientName', 'phoneNumber', 'prospectAddress', 'pinCode', 'state', 'city'],
        []
    );

    const completedRequiredCount = requiredFields.filter((field) => {
        const value = String(formData[field] || '').trim();
        return value && !validateField(field, value, formData);
    }).length;
    const progressPercent = Math.round((completedRequiredCount / requiredFields.length) * 100);

    useEffect(() => {
        const timer = window.setTimeout(() => setPageLoading(false), 350);
        return () => window.clearTimeout(timer);
    }, []);

    useEffect(() => {
        const fetchAutoGeneratedId = async () => {
            try {
                const response = await api.get(`${API_BASE_URL}/api/client/next-client-id`);
                setFormData((prev) => ({
                    ...prev,
                    autoGeneratedId: response.data.autoGeneratedId
                }));
            } catch (err) {
                console.error('Failed to fetch auto-generated ID:', err);
                setErrors((prev) => ({ ...prev, autoGeneratedId: 'Failed to fetch auto-generated ID.' }));
            }
        };

        fetchAutoGeneratedId();
    }, []);

    useEffect(() => {
        const pin = formData.pinCode;
        if (pin.length !== 6) {
            setPinStatus('');
            if (pin.length === 0) setApiLocationSuccess(true);
            return undefined;
        }

        const timer = window.setTimeout(() => fetchLocationByPincode(pin), 450);
        return () => window.clearTimeout(timer);
    }, [formData.pinCode]);

    if (!isAuthenticated()) {
        return <Navigate to="/" />;
    }

    function calculateAge(dob) {
        if (!dob) return null;
        const birthDate = new Date(dob);
        const today = new Date();
        let age = today.getFullYear() - birthDate.getFullYear();
        const monthOffset = today.getMonth() - birthDate.getMonth();
        if (monthOffset < 0 || (monthOffset === 0 && today.getDate() < birthDate.getDate())) {
            age -= 1;
        }
        return age;
    }

    function validateField(name, value, data = formData) {
        let error = '';
        if (!name) return '';

        if (name === 'clientName' && !value.trim()) error = 'Prospect name is required.';
        if (name === 'phoneNumber' && !value) error = 'Phone number is required.';
        if (name === 'phoneNumber' && value && !/^[6-9]\d{9}$/.test(value)) {
            error = 'Enter a valid 10-digit mobile number.';
        }
        if (name === 'emailId' && value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
            error = 'Enter a valid email address.';
        }
        if (name === 'prospectAddress' && !value.trim()) error = 'Address is required.';
        if (name === 'pinCode' && !/^[1-9][0-9]{5}$/.test(value)) {
            error = 'Enter a valid 6-digit PIN code.';
        }
        if (name === 'state' && !value.trim()) error = 'State is required.';
        if (name === 'city' && !value.trim()) error = 'City is required.';
        if (name === 'aadharNumber' && value && !/^\d{12}$/.test(value)) {
            error = 'Aadhar must be exactly 12 digits.';
        }
        if ((name === 'panNumber' || name.endsWith('_PANNumber')) && value && !/^[A-Z]{5}\d{4}[A-Z]{1}$/.test(value)) {
            error = 'Enter a valid PAN number.';
        }
        if (name === 'incomeYearly' && value && Number(value) < 0) {
            error = 'Income cannot be negative.';
        }

        const age = calculateAge(data.prospectDOB);
        const income = Number(data.incomeYearly || 0);
        if (name === 'panNumber' && (age >= 18 || income >= 500000) && !value) {
            error = 'PAN is mandatory for adults or high income.';
        }

        if (name.startsWith('family_') || name.startsWith('child_')) {
            const [, , key] = name.split('_');
            if (key === 'phoneNumber' && value && !/^[6-9]\d{9}$/.test(value)) {
                error = 'Enter a valid 10-digit mobile number.';
            }
            if (key === 'emailId' && value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
                error = 'Enter a valid email address.';
            }
            if (key === 'aadharNumber' && value && !/^\d{12}$/.test(value)) {
                error = 'Aadhar must be exactly 12 digits.';
            }
            if (key === 'PANNumber' && value && !/^[A-Z]{5}\d{4}[A-Z]{1}$/.test(value)) {
                error = 'Enter a valid PAN number.';
            }
        }

        return error;
    }

    const showToast = (type, message) => {
        (notify[type] || notify.info)(message);
    };

    const fetchLocationByPincode = async (pin) => {
        if (pin.length !== 6 || Number.isNaN(Number(pin))) {
            setApiLocationSuccess(false);
            setFormData((prev) => ({ ...prev, city: '', state: '' }));
            return;
        }

        setPinStatus('fetching');
        try {
            const res = await axios.get(`https://api.postalpincode.in/pincode/${pin}`);
            const data = res.data?.[0];

            if (data?.Status === 'Success' && data.PostOffice?.length > 0) {
                const postOffice = data.PostOffice[0];
                setFormData((prev) => ({
                    ...prev,
                    post: postOffice.Name,
                    city: postOffice.District,
                    state: postOffice.State
                }));
                setApiLocationSuccess(true);
                setPinStatus('success');
                setFallbackCities([...new Set(data.PostOffice.map((p) => p.District))]);
                setErrors((prev) => ({ ...prev, pinCode: '', city: '', state: '' }));
            } else {
                setApiLocationSuccess(false);
                setPinStatus('error');
                setFormData((prev) => ({ ...prev, city: '', state: '' }));
                setErrors((prev) => ({ ...prev, pinCode: 'Invalid PIN code. City/state not found.' }));
            }
        } catch (error) {
            console.error('Error fetching PIN code:', error);
            setApiLocationSuccess(false);
            setPinStatus('error');
            setFormData((prev) => ({ ...prev, city: '', state: '' }));
            setErrors((prev) => ({ ...prev, pinCode: 'Unable to fetch location details.' }));
        }
    };

    const normalizeValue = (name, value) => {
        if (['phoneNumber', 'pinCode', 'aadharNumber'].includes(name) || name.endsWith('_aadharNumber')) {
            return value.replace(/\D/g, '');
        }
        if (name === 'panNumber' || name.endsWith('_PANNumber')) {
            return value.toUpperCase();
        }
        return value;
    };

    const handleChange = (e) => {
        const { name, value, type, checked, files } = e.target;
        if (!name) return;

        if (name.startsWith('family_')) {
            const [, index, key] = name.split('_');
            const normalizedValue = normalizeValue(name, value);
            const updatedFamilyMembers = [...formData.familyMembers];
            updatedFamilyMembers[index] = {
                ...updatedFamilyMembers[index],
                [key]: normalizedValue
            };

            setFormData((prev) => ({ ...prev, familyMembers: updatedFamilyMembers }));
            setErrors((prev) => ({
                ...prev,
                [name]: validateField(name, normalizedValue)
            }));
            return;
        }

        if (name.startsWith('child_')) {
            const [, index, key] = name.split('_');
            const normalizedValue = normalizeValue(name, value);
            const updatedChildren = [...formData.children];
            updatedChildren[index] = {
                ...updatedChildren[index],
                [key]: normalizedValue
            };

            setFormData((prev) => ({ ...prev, children: updatedChildren }));
            setErrors((prev) => ({
                ...prev,
                [name]: validateField(name, normalizedValue)
            }));
            return;
        }

        if (type === 'checkbox') {
            if (name.startsWith('toggle_')) {
                const index = Number(name.split('_')[1]);
                const updatedFamilyMembers = [...formData.familyMembers];
                updatedFamilyMembers[index] = {
                    ...updatedFamilyMembers[index],
                    showDetails: checked
                };
                setFormData((prev) => ({ ...prev, familyMembers: updatedFamilyMembers }));
            } else if (name === 'showChildren') {
                setFormData((prev) => ({
                    ...prev,
                    showChildren: checked,
                    children: checked ? prev.children : []
                }));
            }
            return;
        }

        if (type === 'file') {
            const file = files?.[0] || null;
            setFormData((prev) => ({
                ...prev,
                [name]: file,
                [name === 'aadharFile' ? 'aadharFileUpload' : 'panFileUpload']: Boolean(file)
            }));
            setErrors((prev) => ({ ...prev, [name]: '' }));
            if (file) showToast('success', `${file.name} selected.`);
            return;
        }

        const normalizedValue = normalizeValue(name, value);
        const nextFormData = {
            ...formData,
            [name]: normalizedValue
        };

        if (name === 'pinCode' && normalizedValue.length < 6) {
            nextFormData.city = '';
            nextFormData.state = '';
            setPinStatus('');
        }

        setFormData(nextFormData);
        setErrors((prev) => ({
            ...prev,
            [name]: validateField(name, normalizedValue, nextFormData)
        }));
    };

    const addChild = () => {
        setFormData((prev) => ({
            ...prev,
            children: [
                ...prev.children,
                { name: '', phoneNumber: '', emailId: '', DOB: '', gender: '', occupation: '', PANNumber: '', aadharNumber: '' }
            ]
        }));
    };

    const removeChild = (index) => {
        setFormData((prev) => ({
            ...prev,
            children: prev.children.filter((_, i) => i !== index)
        }));
    };

    const validateForm = () => {
        const newErrors = {};

        requiredFields.forEach((key) => {
            const error = validateField(key, formData[key]);
            if (error) newErrors[key] = error;
        });

        ['emailId', 'incomeYearly', 'aadharNumber', 'panNumber'].forEach((key) => {
            const error = validateField(key, formData[key]);
            if (error) newErrors[key] = error;
        });

        formData.familyMembers.forEach((member, index) => {
            if (!member.showDetails) return;
            Object.keys(member).forEach((key) => {
                if (key === 'relation' || key === 'showDetails') return;
                const fieldName = `family_${index}_${key}`;
                const error = validateField(fieldName, member[key]);
                if (error) newErrors[fieldName] = error;
            });
        });

        if (formData.showChildren) {
            formData.children.forEach((child, index) => {
                if (!child.name) newErrors[`child_${index}_name`] = 'Child name is required.';
                Object.keys(child).forEach((key) => {
                    const fieldName = `child_${index}_${key}`;
                    const error = validateField(fieldName, child[key]);
                    if (error) newErrors[fieldName] = error;
                });
            });
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const resetForm = async () => {
        setFormData(initialFormData);
        setErrors({});
        setPinStatus('');
        setApiLocationSuccess(true);
        setFallbackCities([]);

        try {
            const response = await api.get(`${API_BASE_URL}/api/client/next-client-id`);
            setFormData((prev) => ({
                ...prev,
                autoGeneratedId: response.data.autoGeneratedId
            }));
        } catch (error) {
            console.error('Failed to refresh client ID:', error);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            showToast('error', 'Please fix the highlighted fields.');
            return;
        }

        const confirmed = await confirmDialog({ title: 'Save retail client?', message: 'Please confirm the details before creating this client record.', confirmText: 'Save Client', cancelText: 'Review Again', tone: 'info' });
        if (!confirmed) return;

        const formDataToSubmit = new FormData();
        Object.keys(formData).forEach((key) => {
            const value = formData[key];
            if (value === undefined || value === null || value === '') return;

            if (key === 'aadharFile' || key === 'panFile') {
                if (value instanceof File) formDataToSubmit.append(key, value);
            } else if (key === 'familyMembers' || key === 'children') {
                formDataToSubmit.append(key, JSON.stringify(value));
            } else if (typeof value === 'boolean') {
                formDataToSubmit.append(key, value.toString());
            } else if (typeof value === 'object') {
                formDataToSubmit.append(key, JSON.stringify(value));
            } else {
                formDataToSubmit.append(key, value);
            }
        });

        try {
            setLoading(true);
            const response = await api.post(`${API_BASE_URL}/api/client`, formDataToSubmit, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            if (response.status === 201) {
                showToast('success', `Retail client saved successfully. Client ID: ${formData.autoGeneratedId}`);
                await resetForm();
            } else {
                showToast('error', 'Something went wrong. Please try again.');
            }
        } catch (error) {
            console.error('Error submitting data:', error);
            showToast('error', error.response?.data?.error || 'Error submitting data. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const renderError = (name) => (
        errors[name] ? (
            <span className="rcf-error">
                <CircleAlert size={14} />
                {errors[name]}
            </span>
        ) : null
    );

    const renderField = ({
        name,
        label,
        type = 'text',
        placeholder,
        required = false,
        readOnly = false,
        multiline = false,
        options,
        className = ''
    }) => {
        const FieldTag = options ? 'select' : multiline ? 'textarea' : 'input';
        return (
            <div className={`rcf-field ${className} ${errors[name] ? 'has-error' : ''}`}>
                <label htmlFor={name}>
                    {label}
                    {required && <span>*</span>}
                </label>
                <div className="rcf-input-shell">
                    <FieldTag
                        id={name}
                        name={name}
                        type={options || multiline ? undefined : type}
                        value={formData[name] || ''}
                        placeholder={placeholder}
                        readOnly={readOnly}
                        onChange={handleChange}
                    >
                        {options && options.map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </FieldTag>
                </div>
                {renderError(name)}
            </div>
        );
    };

    const renderFileUpload = (name, label, file) => (
        <div className="rcf-upload-card">
            <div>
                <UploadCloud size={28} />
                <strong>{label}</strong>
                <span>PDF or image files supported</span>
            </div>
            <button
                type="button"
                className="rcf-upload-button"
                onClick={() => fileRefs.current[name]?.click()}
            >
                <UploadCloud size={16} />
                Upload
            </button>
            <input
                ref={(element) => {
                    fileRefs.current[name] = element;
                }}
                type="file"
                name={name}
                onChange={handleChange}
                accept="application/pdf,image/*"
                hidden
            />
            {file && (
                <div className="rcf-file-preview">
                    <FileText size={16} />
                    <span>{file.name}</span>
                    <button
                        type="button"
                        onClick={() =>
                            setFormData((prev) => ({
                                ...prev,
                                [name]: null,
                                [name === 'aadharFile' ? 'aadharFileUpload' : 'panFileUpload']: false
                            }))
                        }
                        aria-label={`Remove ${file.name}`}
                    >
                        <X size={14} />
                    </button>
                </div>
            )}
        </div>
    );

    const renderRelativeFields = (prefix, person, index) => (
        <div className="rcf-grid compact">
            {Object.keys(person).filter((key) => key !== 'relation' && key !== 'showDetails').map((key) => {
                const fieldName = `${prefix}_${index}_${key}`;
                const isDate = key === 'DOB';
                const isSelect = key === 'gender';
                return (
                    <div className={`rcf-field ${errors[fieldName] ? 'has-error' : ''}`} key={fieldName}>
                        <label htmlFor={fieldName}>{labelMap[key] || key}</label>
                        <div className="rcf-input-shell">
                            {isSelect ? (
                                <select id={fieldName} name={fieldName} value={person[key] || ''} onChange={handleChange}>
                                    <option value="">Select Gender</option>
                                    <option value="Male">Male</option>
                                    <option value="Female">Female</option>
                                    <option value="Other">Other</option>
                                </select>
                            ) : (
                                <input
                                    id={fieldName}
                                    type={isDate ? 'date' : 'text'}
                                    name={fieldName}
                                    value={person[key] || ''}
                                    onChange={handleChange}
                                />
                            )}
                        </div>
                        {renderError(fieldName)}
                    </div>
                );
            })}
        </div>
    );

    const visibleFamilyMembers = formData.familyMembers.filter((member) =>
        member.relation === 'father' ||
        member.relation === 'mother' ||
        (['Married', 'Divorced'].includes(formData.maritalStatus) &&
            ['spouse', 'fatherInLaw', 'motherInLaw'].includes(member.relation))
    );

    return (
        <div className="client-container retail-client-shell">
            <div className="sidebar">
                <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} />
            </div>
            <div className={`client-content retail-client-content ${collapsed ? 'collapsed' : ''}`}>
                <Header />

                <main className="rcf-page">
                    {pageLoading ? (
                        <div className="rcf-skeleton-panel">
                            <div className="rcf-skeleton rcf-skeleton-title" />
                            <div className="rcf-skeleton-grid">
                                {Array.from({ length: 8 }).map((_, index) => (
                                    <div className="rcf-skeleton-card" key={index} />
                                ))}
                            </div>
                        </div>
                    ) : (
                        <>
                            <section className="rcf-hero">
                                <div>
                                    <span className="rcf-eyebrow">Client Management</span>
                                    <h1>Retail Client Intake</h1>
                                    {/* <p>Capture prospect, contact, KYC, family, and location details in a guided workspace.</p> */}
                                </div>
                                <div className="rcf-progress-card">
                                    <span>{progressPercent}% Complete</span>
                                    <div className="rcf-progress-track">
                                        <div style={{ width: `${progressPercent}%` }} />
                                    </div>
                                    <small>{completedRequiredCount} of {requiredFields.length} required fields ready</small>
                                </div>
                            </section>

                            <form className="rcf-form" onSubmit={handleSubmit}>
                                <section className="rcf-card rcf-fade-in">
                                    <div className="rcf-section-heading">
                                        <span><IdCard size={20} /></span>
                                        <div>
                                            <h2>Client Details</h2>
                                            <p>Retail prospect identity and generated client reference.</p>
                                        </div>
                                    </div>
                                    <div className="rcf-grid">
                                        {renderField({
                                            name: 'autoGeneratedId',
                                            label: 'Client ID',
                                            readOnly: true,
                                            placeholder: 'Auto-generated'
                                        })}
                                        {renderField({
                                            name: 'clientName',
                                            label: 'Prospect Name',
                                            required: true,
                                            placeholder: 'Full name'
                                        })}
                                        <div className="rcf-field">
                                            <label htmlFor="clientType">Client Type</label>
                                            <div className="rcf-input-shell rcf-locked-field">
                                                <input id="clientType" type="text" value="Retail" readOnly />
                                            </div>
                                        </div>
                                    </div>
                                </section>

                                <section className="rcf-card rcf-fade-in delay-1">
                                    <div className="rcf-section-heading">
                                        <span><Contact size={20} /></span>
                                        <div>
                                            <h2>Contact Details</h2>
                                            <p>Primary communication details for policy servicing.</p>
                                        </div>
                                    </div>
                                    <div className="rcf-grid">
                                        {renderField({
                                            name: 'phoneNumber',
                                            label: 'Contact No',
                                            required: true,
                                            placeholder: '9876543210'
                                        })}
                                        {renderField({
                                            name: 'emailId',
                                            label: 'Email ID',
                                            type: 'email',
                                            placeholder: 'name@example.com'
                                        })}
                                    </div>
                                </section>

                                <section className="rcf-card rcf-fade-in delay-2">
                                    <div className="rcf-section-heading">
                                        <span><MapPin size={20} /></span>
                                        <div>
                                            <h2>Address Information</h2>
                                            <p>City and state auto-fill when a valid Indian PIN code is entered.</p>
                                        </div>
                                    </div>
                                    <div className="rcf-grid">
                                        {renderField({
                                            name: 'prospectAddress',
                                            label: 'Address',
                                            required: true,
                                            multiline: true,
                                            className: 'rcf-wide',
                                            placeholder: 'House, street, landmark'
                                        })}
                                        <div className={`rcf-field ${errors.pinCode ? 'has-error' : ''}`}>
                                            <label htmlFor="pinCode">PIN Code <span>*</span></label>
                                            <div className="rcf-input-shell rcf-location-input">
                                                <input
                                                    id="pinCode"
                                                    type="text"
                                                    name="pinCode"
                                                    value={formData.pinCode}
                                                    maxLength="6"
                                                    inputMode="numeric"
                                                    placeholder="400001"
                                                    onChange={handleChange}
                                                />
                                                {pinStatus === 'fetching' && <Loader2 className="rcf-spin" size={17} />}
                                                {pinStatus === 'success' && <CheckCircle2 className="rcf-valid" size={17} />}
                                            </div>
                                            {renderError('pinCode')}
                                        </div>
                                        {apiLocationSuccess ? (
                                            <>
                                                {renderField({ name: 'city', label: 'City', required: true, readOnly: true, placeholder: 'Auto-filled' })}
                                                {renderField({ name: 'state', label: 'State', required: true, readOnly: true, placeholder: 'Auto-filled' })}
                                            </>
                                        ) : (
                                            <>
                                                {renderField({
                                                    name: 'city',
                                                    label: 'City',
                                                    required: true,
                                                    options: [
                                                        { value: '', label: 'Select City' },
                                                        ...(fallbackCities.length
                                                            ? fallbackCities.map((city) => ({ value: city, label: city }))
                                                            : [{ value: 'Unknown', label: 'Unknown' }])
                                                    ]
                                                })}
                                                {renderField({
                                                    name: 'state',
                                                    label: 'State',
                                                    required: true,
                                                    options: [
                                                        { value: '', label: 'Select State' },
                                                        ...fallbackStates.map((state) => ({ value: state, label: state }))
                                                    ]
                                                })}
                                            </>
                                        )}
                                    </div>
                                </section>

                                <section className="rcf-card rcf-fade-in delay-3">
                                    <div className="rcf-section-heading">
                                        <span><UserRound size={20} /></span>
                                        <div>
                                            <h2>Personal Information</h2>
                                            <p>Demographic and occupation details used for policy suitability.</p>
                                        </div>
                                    </div>
                                    <div className="rcf-grid">
                                        {renderField({ name: 'prospectDOB', label: 'Date of Birth', type: 'date' })}
                                        {renderField({
                                            name: 'prospectGender',
                                            label: 'Gender',
                                            options: [
                                                { value: '', label: 'Select Gender' },
                                                { value: 'Male', label: 'Male' },
                                                { value: 'Female', label: 'Female' },
                                                { value: 'Other', label: 'Other' }
                                            ]
                                        })}
                                        {renderField({
                                            name: 'maritalStatus',
                                            label: 'Marital Status',
                                            options: [
                                                { value: '', label: 'Select Marital Status' },
                                                { value: 'Single', label: 'Single' },
                                                { value: 'Married', label: 'Married' },
                                                { value: 'Divorced', label: 'Divorced' }
                                            ]
                                        })}
                                        {renderField({
                                            name: 'prospectOccupation',
                                            label: 'Occupation',
                                            options: [
                                                { value: '', label: 'Select Occupation' },
                                                { value: 'Software Developer', label: 'Software Developer' },
                                                { value: 'Sales', label: 'Sales' },
                                                { value: 'Business', label: 'Business' }
                                            ]
                                        })}
                                        {renderField({
                                            name: 'incomeYearly',
                                            label: 'Income Yearly',
                                            type: 'number',
                                            placeholder: 'Annual income'
                                        })}
                                    </div>
                                </section>

                                <section className="rcf-card rcf-fade-in delay-4">
                                    <div className="rcf-section-heading">
                                        <span><BadgeIndianRupee size={20} /></span>
                                        <div>
                                            <h2>KYC & Documents</h2>
                                            <p>Capture identity numbers and upload supporting documents.</p>
                                        </div>
                                    </div>
                                    <div className="rcf-grid">
                                        {renderField({
                                            name: 'aadharNumber',
                                            label: 'Aadhar Number',
                                            placeholder: '12 digit number'
                                        })}
                                        {renderField({
                                            name: 'panNumber',
                                            label: 'PAN Number',
                                            placeholder: 'ABCDE1234F'
                                        })}
                                    </div>
                                    <div className="rcf-upload-grid">
                                        {renderFileUpload('aadharFile', 'Upload Aadhar', formData.aadharFile)}
                                        {renderFileUpload('panFile', 'Upload PAN', formData.panFile)}
                                    </div>
                                </section>

                                <section className="rcf-card rcf-fade-in delay-5">
                                    <div className="rcf-section-heading">
                                        <span><UsersRound size={20} /></span>
                                        <div>
                                            <h2>Family Information</h2>
                                            <p>Add family members only when their details are relevant to the policy workflow.</p>
                                        </div>
                                    </div>
                                    <div className="rcf-family-list">
                                        {visibleFamilyMembers.map((member) => {
                                            const index = formData.familyMembers.findIndex((item) => item.relation === member.relation);
                                            return (
                                                <div className="rcf-family-item" key={member.relation}>
                                                    <label className="rcf-toggle">
                                                        <input
                                                            type="checkbox"
                                                            name={`toggle_${index}`}
                                                            checked={member.showDetails}
                                                            onChange={handleChange}
                                                        />
                                                        <span>{relationLabels[member.relation]}</span>
                                                    </label>
                                                    {member.showDetails && renderRelativeFields('family', member, index)}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </section>

                                {['Married', 'Divorced'].includes(formData.maritalStatus) && (
                                    <section className="rcf-card rcf-fade-in delay-5">
                                        <div className="rcf-section-heading">
                                            <span><HeartHandshake size={20} /></span>
                                            <div>
                                                <h2>Children</h2>
                                                <p>Record child details where they are part of the policy context.</p>
                                            </div>
                                        </div>
                                        <label className="rcf-toggle rcf-children-toggle">
                                            <input
                                                type="checkbox"
                                                name="showChildren"
                                                checked={formData.showChildren}
                                                onChange={handleChange}
                                            />
                                            <span>Add Children</span>
                                        </label>

                                        {formData.showChildren && (
                                            <>
                                                <button type="button" className="rcf-inline-button" onClick={addChild}>
                                                    <Plus size={17} />
                                                    Add Child
                                                </button>
                                                <div className="rcf-child-list">
                                                    {formData.children.map((child, index) => (
                                                        <div className="rcf-child-card" key={index}>
                                                            <div className="rcf-child-heading">
                                                                <h3>Child {index + 1}</h3>
                                                                <button type="button" onClick={() => removeChild(index)} aria-label={`Remove child ${index + 1}`}>
                                                                    <Trash2 size={16} />
                                                                </button>
                                                            </div>
                                                            {renderRelativeFields('child', child, index)}
                                                        </div>
                                                    ))}
                                                </div>
                                            </>
                                        )}
                                    </section>
                                )}

                                <div className="rcf-action-bar">
                                    <button type="button" className="rcf-btn secondary" onClick={resetForm}>
                                        <RotateCcw size={18} />
                                        Reset
                                    </button>
                                    <button type="submit" className="rcf-btn primary" disabled={loading}>
                                        {loading ? <Loader2 className="rcf-spin" size={18} /> : <Send size={18} />}
                                        {loading ? 'Saving Client...' : 'Submit Client'}
                                    </button>
                                </div>
                            </form>
                        </>
                    )}
                </main>

            </div>
        </div>
    );
};

export default Client;
